import {
  createClientSharedComposable,
  getStore,
  useLocalStorageObject,
} from '@global-store/store'
import type { CartStore } from '@global-store/stores/cart/types'
import { useLocationStore } from '@global-store/stores/location'
import { useToastsStore } from '@global-store/stores/toast'
import { sprykerApiClient } from 'internal/api-client'
import { defineStore } from 'pinia'

import { isBrowser, isSSR } from '@fond-of/env'
import { type Locale } from '@fond-of/locale'
import type { CartInterface } from '@fond-of/spryker-api'
import { trackError } from '@fond-of/telemetry'

const toastTranslations: Record<Locale, string> = {
  en: 'Your shopping cart has been updated. Products that are no longer available have been removed.',
  de: 'Dein Warenkorb wurde aktualisiert. Nicht mehr verfügbare Produkte wurden entfernt.',
  fr: 'Votre panier a été mis à jour. Les produits qui ne sont plus disponibles ont été supprimés.',
  da: 'Din indkøbskurv er blevet opdateret. Produkter, der ikke længere er tilgængelige, er blevet fjernet.',
  es: 'Su cesta de la compra ha sido actualizada. Se han eliminado los productos que ya no están disponibles.',
  it: 'Il carrello è stato aggiornato. I prodotti non più disponibili sono stati rimossi.',
}

const toastPriceTranslations: Record<Locale, string> = {
  en: 'Your shopping cart has been updated. The price has changed for some products.',
  de: 'Dein Warenkorb wurde aktualisiert. Für manche Produkte hat sich der Preis geändert.',
  fr: 'Ton panier a été mis à jour. Le prix de certains produits a changé.',
  da: 'Din indkøbskurv er blevet opdateret. Prisen er ændret for nogle produkter.',
  es: 'Su cesta de la compra ha sido actualizada. El precio de algunos productos ha cambiado.',
  it: 'Il carrello è stato aggiornato. Il prezzo di alcuni prodotti è cambiato.',
}

const cleanUpCart = async (newCart?: CartInterface) => {
  const toastsStore = useToastsStore()
  const locationStore = useLocationStore()

  if (!newCart) {
    return false
  }

  const disabledCartItems = newCart.cartItems.filter(
    (cartItem) => !cartItem.concreteProducts?.[0]
  )

  if (!disabledCartItems.length) {
    return false
  }

  await Promise.all(
    disabledCartItems.map((disabledCartItem) =>
      sprykerApiClient.removeCartItem(newCart.id, disabledCartItem.id)
    )
  )

  toastsStore.addWarningToast(toastTranslations[locationStore.currentLocale])

  const activeCartItems = newCart.cartItems.filter(
    (cartItem) => !!cartItem.concreteProducts?.[0]
  )

  return {
    ...newCart,
    cartItems: activeCartItems,
  }
}

/**
 * Validates if the price of the cart items has changed
 * if so, add a message to the toast store and rerturn the new cart
 *
 * @param oldCart
 * @param newCart
 *
 * @returns Promise<CartInterface | false>
 */
const validatePriceChange = (
  oldCart: CartInterface | null,
  newCart?: CartInterface
) => {
  const toastsStore = useToastsStore()
  const locationStore = useLocationStore()

  if (!newCart || !oldCart) {
    return false
  }

  const priceChangedCartItems = newCart.cartItems.filter((cartItem) => {
    const oldCartItem = oldCart.cartItems.find(
      (oldCartItem) => oldCartItem.id === cartItem.id
    )

    if (!oldCartItem) {
      return false
    }

    return (
      oldCartItem.calculations.unitPrice !== cartItem.calculations.unitPrice
    )
  })

  if (!priceChangedCartItems.length) {
    return false
  }

  toastsStore.addWarningToast(
    toastPriceTranslations[locationStore.currentLocale]
  )

  return true
}

export const useCartStore = createClientSharedComposable(() =>
  defineStore('legacyCart', {
    state: (): CartStore => ({
      reloadCart: false,
      errorMessage: undefined,
      isLoading: true,
      cart: useLocalStorageObject<CartInterface | null>('cart', null),
    }),

    getters: {
      itemIds: (state) => {
        return [...new Set(state.cart?.cartItems.map(({ id }) => id))]
      },

      containsOnlyCaseable: (state) => {
        return !!state.cart?.cartItems.every(
          (item) => item.concreteProducts[0].attributes.caseable_sku
        )
      },
      containsCaseable: (state) => {
        return !!state.cart?.cartItems.some(
          (item) => item.concreteProducts[0].attributes.caseable_sku
        )
      },

      containsOnlyVouchers: (state) => {
        return !!state.cart?.cartItems.every(
          (cartItem) =>
            cartItem.concreteProducts?.[0]?.attributes.model_key === 'voucher'
        )
      },
      containsVouchers: (state) => {
        return !!state.cart?.cartItems.some(
          (cartItem) =>
            cartItem.concreteProducts?.[0]?.attributes.model_key === 'voucher'
        )
      },

      hasGiftCards: (state) => {
        return (state.cart?.giftCards?.length || 0) > 0
      },
      giftCards: (state) => {
        return state.cart?.giftCards || []
      },

      quantity: (state) =>
        state.cart?.cartItems?.reduce(
          (quantity, cartItem) => quantity + cartItem.quantity,
          0
        ) || 0,

      hasCartItems: (state) => !!state.cart?.cartItems.length,
      isCartItemsEmpty: (state) => !state.cart?.cartItems.length,

      hasPromotionItems: (state) => !!state.cart?.promotionalItems?.length,
      isPromotionItemsEmpty: (state) => !state.cart?.promotionalItems?.length,

      isFreeShipping: (state) => state.cart?.totals.shipmentTotal === 0,
    },

    actions: {
      waitForCart() {
        return new Promise((reslove) =>
          this.$subscribe(() => !this.isLoading && reslove(this), {
            immediate: true,
          })
        )
      },
      setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading
      },
      /**
       * Sets new cart in store and pre validates if items in cart
       * have been disabled or price of the items has changed
       * if so,the cart will be updated and a toast will be shown
       *
       * @param cart
       * @returns Promise<CartInterface | undefined>
       */
      async setCart(cart?: CartInterface) {
        try {
          if (cart && validatePriceChange(this.cart, cart)) {
            await sprykerApiClient.reloadCart(
              cart.id,
              import.meta.env.PUBLIC_STORE
            )
          }

          const cleanedUpCart = await cleanUpCart(cart)

          if (cleanedUpCart) {
            cart = await sprykerApiClient.getCartById(cleanedUpCart.id)
          }

          this.cart = cart || null

          return cart
        } catch (exception) {
          trackError(exception)
          this.$reset()
          isBrowser() && window.location.reload()
        }
      },

      $reset() {
        this.cart = null

        if (isSSR()) {
          return
        }

        localStorage.removeItem('cart')
      },
    },

    hydrate: (storeState) => {
      // @ts-expect-error: https://github.com/microsoft/TypeScript/issues/43826
      storeState.cart = useLocalStorageObject<CartInterface | undefined>(
        'cart',
        undefined
      )
    },
  })(getStore())
)
